import React, { useState } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Table,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  GET_APPOINTMENT_TRANSACTIONS
} from '../../graphql/queries';
import {
  UPDATE_TRANSACTION,
  DELETE_TRANSACTION
} from '../../graphql/mutations';
import CreateTransactionDialog from './CreateTransactionDialog';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface TransactionSectionProps {
  appointmentId: string;
  transactions?: any[]; // Optional: if provided, use these instead of fetching
  transactionsLoading?: boolean; // Optional: loading state if transactions are provided
  onRefetch?: () => void; // Optional: refetch callback if transactions are provided
  onTransactionCreated?: (transaction: any) => void;
  onTransactionUpdated?: (transaction: any) => void;
  onTransactionDeleted?: (transactionId: string) => void;
}

const TransactionSection: React.FC<TransactionSectionProps> = ({
  appointmentId,
  transactions: providedTransactions,
  transactionsLoading: providedLoading,
  onRefetch,
  onTransactionCreated,
  onTransactionUpdated,
  onTransactionDeleted
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<any>(null);
  const [form] = Form.useForm();

  // Fetch transactions for this appointment only if not provided as prop
  const { data: transactionsData, loading: transactionsLoading, refetch: refetchTransactions } = useQuery(GET_APPOINTMENT_TRANSACTIONS, {
    variables: { appointmentId },
    skip: !appointmentId || providedTransactions !== undefined,
  });

  // Mutations
  const [updateTransaction, { loading: updating }] = useMutation(UPDATE_TRANSACTION);
  const [deleteTransaction, { loading: deleting }] = useMutation(DELETE_TRANSACTION);

  // Use provided transactions or fetch from query
  const transactions = providedTransactions !== undefined ? providedTransactions : (transactionsData?.appointmentTransactions || []);
  const isLoading = providedLoading !== undefined ? providedLoading : transactionsLoading;
  
  // Use provided refetch or local refetch
  const handleRefetch = onRefetch || refetchTransactions;

  // Calculate local stats for this appointment's transactions
  // Use original_amount when available to show full transaction value
  const appointmentStats = {
    totalTransactions: transactions.length,
    totalAmount: transactions
      .reduce((sum: number, t: any) => sum + (t.original_amount || t.final_amount || t.amount || 0), 0),
    totalPayments: transactions
      .filter((t: any) => t.status === 'completed')
      .reduce((sum: number, t: any) => sum + (t.amount || 0), 0),
    totalCompleted: transactions
      .filter((t: any) => t.status === 'completed')
      .reduce((sum: number, t: any) => sum + (t.original_amount || t.amount || 0), 0),
    averageAmount: transactions.length > 0
      ? transactions.reduce((sum: number, t: any) => sum + (t.original_amount || t.final_amount || t.amount || 0), 0) / transactions.length
      : 0
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'completed': return 'green';
      case 'failed': return 'red';
      case 'cancelled': return 'default';
      case 'refunded': return 'blue';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ClockCircleOutlined />;
      case 'completed': return <CheckCircleOutlined />;
      case 'failed': return <CloseCircleOutlined />;
      case 'cancelled': return <CloseCircleOutlined />;
      case 'refunded': return <ExclamationCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'qfpay_checkout': return <CreditCardOutlined />;
      case 'development': return <DollarOutlined />;
      default: return <DollarOutlined />;
    }
  };

  const handleCreateSuccess = (transaction: any) => {
    window.location.reload();
    // setIsCreateModalVisible(false);
    // onTransactionCreated?.(transaction);
  };

  const handleUpdateTransaction = async (values: any) => {
    if (!editingTransaction) return;

    try {
      const result = await updateTransaction({
        variables: {
          id: editingTransaction.id,
          updateTransactionDto: values
        }
      });

      message.success(t('appointments.transactions.transactionUpdated'));
      setIsEditModalVisible(false);
      setEditingTransaction(null);
      form.resetFields();
      handleRefetch();
      onTransactionUpdated?.(result.data.updateTransaction);
    } catch (err: any) {
      message.error(err?.message || t('appointments.transactions.transactionUpdateFailed'));
    }
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    try {
      await deleteTransaction({
        variables: { id: transactionId }
      });

      message.success(t('appointments.transactions.transactionDeleted'));
      handleRefetch();
      onTransactionDeleted?.(transactionId);
    } catch (err: any) {
      message.error(err?.message || t('appointments.transactions.transactionDeleteFailed'));
    }
  };

  const handleEditTransaction = (transaction: any) => {
    setEditingTransaction(transaction);
    form.setFieldsValue({
      amount: transaction.amount,
      description: transaction.description,
      category: transaction.category,
      subcategory: transaction.subcategory,
      notes: transaction.notes,
      payment_method: transaction.payment_method
    });
    setIsEditModalVisible(true);
  };

  const handleViewTransaction = (transaction: any) => {
    navigate(`/transactions/${transaction.id}`);
  };

  const handleCopyTransactionLink = async (transaction: any) => {
    try {
      // Copy QFPay checkout URL (button only shows when this exists)
      const qfpayUrl = transaction.metadata?.qfpay?.checkout_url;

      await navigator.clipboard.writeText(qfpayUrl);
      message.success(t('appointments.transactionLinkCopied'));
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      try {
        const qfpayUrl = transaction.metadata?.qfpay?.checkout_url;

        const textArea = document.createElement('textarea');
        textArea.value = qfpayUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success(t('appointments.transactionLinkCopied'));
      } catch (fallbackErr) {
        message.error(t('appointments.transactionLinkCopyFailed'));
      }
    }
  };

  const columns = [
    {
      title: t('appointments.transactions.reference'),
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      render: (reference: string) => (
        <Text code>{reference?.slice(-8)}</Text>
      )
    },
    {
      title: t('appointments.transactions.originalAmount'),
      dataIndex: 'original_amount',
      key: 'original_amount',
      render: (originalAmount: number, record: any) => (
        <Space>
          <Text>${originalAmount?.toFixed(2)}</Text>
          <Text type="secondary">{record.currency}</Text>
        </Space>
      )
    },
    {
      title: t('appointments.transactions.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Space>
          <Text strong>${amount?.toFixed(2)}</Text>
          <Text type="secondary">{record.currency}</Text>
        </Space>
      )
    },
    {
      title: t('appointments.transactions.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {t(`appointments.transactions.statuses.${status}`) || status}
        </Tag>
      )
    },
    // {
    //   title: 'Payment Method',
    //   dataIndex: 'payment_method',
    //   key: 'payment_method',
    //   render: (method: string) => (
    //     method ? (
    //       <Space>
    //         {getPaymentMethodIcon(method)}
    //         <Text>{method}</Text>
    //       </Space>
    //     ) : (
    //       <Text type="secondary">N/A</Text>
    //     )
    //   )
    // },
    {
      title: t('appointments.transactions.description'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Tooltip title={description}>
          <Text ellipsis style={{ maxWidth: 150 }}>
            {description || t('common.na')}
          </Text>
        </Tooltip>
      )
    },
    {
      title: t('appointments.transactions.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <Text>{dayjs(date).format('YYYY/MM/DD HH:mm')}</Text>
      )
    },
    {
      title: t('appointments.transactions.actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewTransaction(record)}
            title={t('appointments.transactions.view')}
          />
          {record.metadata?.qfpay?.checkout_url && (
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCopyTransactionLink(record)}
              title={t('appointments.transactions.copyLink')}
            />
          )}
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditTransaction(record)}
            title={t('appointments.transactions.edit')}
          />
          <Popconfirm
            title={t('appointments.transactions.deleteConfirm')}
            onConfirm={() => handleDeleteTransaction(record.id)}
            okText={t('appointments.transactions.delete')}
            cancelText={t('appointments.transactions.cancel')}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title={t('appointments.transactions.delete')}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <DollarOutlined />
            {t('appointments.transactions.title')}
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalVisible(true)}
          >
            {t('appointments.transactions.createTransaction')}
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        {/* Transaction Stats */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Title level={5}>{t('appointments.transactions.appointmentTransactions')}</Title>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title={t('appointments.transactions.totalTransactions')}
                  value={appointmentStats.totalTransactions}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('appointments.transactions.totalAmount')}
                  value={appointmentStats.totalAmount}
                  prefix="$"
                  precision={2}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('appointments.transactions.totalCompleted')}
                  value={`$ ${appointmentStats.totalCompleted.toFixed(2)}`}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<DollarOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('appointments.transactions.totalPayments')}
                  value={`$ ${appointmentStats.totalPayments.toFixed(2)}`}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<DollarOutlined />}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Transactions Table */}
        <Table
          columns={columns}
          dataSource={transactions}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} items`
          }}
          locale={{
            emptyText: t('appointments.transactions.noTransactionsFound')
          }}
        />
      </Card>

      {/* Create Transaction Dialog */}
      <CreateTransactionDialog
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        appointmentId={appointmentId}
        onSuccess={handleCreateSuccess}
        onRefetch={handleRefetch}
      />

      {/* Edit Transaction Modal */}
      <Modal
        title={t('appointments.transactions.editTransaction')}
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingTransaction(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateTransaction}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label={t('appointments.transactions.amount')}
                rules={[
                  { required: true, message: t('appointments.transactions.amountRequired') },
                  { type: 'number', min: 0, message: t('appointments.transactions.amountMustBePositive') }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  precision={2}
                  prefix="$"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="payment_method"
                label={t('appointments.transactions.paymentMethod')}
              >
                <Select
                  placeholder={t('appointments.transactions.selectPaymentMethod')}
                  allowClear
                >
                  {/* <Select.Option value="development">{t('appointments.transactions.paymentMethods.development')}</Select.Option> */}
                  <Select.Option value="qfpay_checkout">{t('appointments.transactions.paymentMethods.qfpay_checkout')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t('appointments.transactions.description')}
            rules={[
              { required: true, message: t('appointments.transactions.descriptionRequired') }
            ]}
          >
            <Input placeholder={t('appointments.transactions.enterDescription')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label={t('appointments.transactions.category')}
              >
                <Select
                  placeholder={t('appointments.transactions.selectCategory')}
                  allowClear
                >
                  <Select.Option value="CONSULTATION">{t('appointments.transactions.categories.CONSULTATION')}</Select.Option>
                  <Select.Option value="MEDICATION">{t('appointments.transactions.categories.MEDICATION')}</Select.Option>
                  <Select.Option value="LAB_TEST">{t('appointments.transactions.categories.LAB_TEST')}</Select.Option>
                  <Select.Option value="DIAGNOSTIC_TEST">{t('appointments.transactions.categories.DIAGNOSTIC_TEST')}</Select.Option>
                  <Select.Option value="SURGERY">{t('appointments.transactions.categories.SURGERY')}</Select.Option>
                  <Select.Option value="DELIVERY">{t('appointments.transactions.categories.DELIVERY')}</Select.Option>
                  <Select.Option value="OTHER">{t('appointments.transactions.categories.OTHER')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="subcategory"
                label={t('appointments.transactions.subcategory')}
              >
                <Input placeholder={t('appointments.transactions.enterSubcategory')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label={t('appointments.transactions.notes')}
          >
            <TextArea
              rows={3}
              placeholder={t('appointments.transactions.enterNotes')}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={updating}
              >
                {t('appointments.transactions.update')}
              </Button>
              <Button
                onClick={() => {
                  setIsEditModalVisible(false);
                  setEditingTransaction(null);
                  form.resetFields();
                }}
              >
                {t('appointments.transactions.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TransactionSection;
